import { useState } from 'react'
import { Table, Button, Row, Col, Form } from 'react-bootstrap'
import { FaEdit, FaPlus, FaTrash } from 'react-icons/fa'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Message from '../../components/Message'
import Loader from '../../components/Loader'
import Paginate from '../../components/Paginate'
import {
  useGetCustomersQuery,
  useDeleteCustomerMutation,
  useCreateCustomerMutation,
} from '../../slices/customersApiSlice'
import { useGetExpensesQuery } from '../../slices/expensesApiSlice'
import * as XLSX from 'xlsx'


const CustomerListScreen = () => {
  const { pageNumber } = useParams()

  // Default date range: Jan 1st of this year → today
  const today = new Date().toISOString().split('T')[0]
  const jan1  = new Date(new Date().getFullYear(), 0, 1)
    .toISOString()
    .split('T')[0]

  const [startDate, setStartDate] = useState(jan1)
  const [endDate,   setEndDate]   = useState(today)
  const [filter,    setFilter]    = useState('')
  const [sortField, setSortField] = useState('')
  const [sortOrder, setSortOrder] = useState('asc')

  // Fetch customers
  const {
    data: customerData,
    isLoading: loadingCustomers,
    error: customersError,
    refetch: refetchCustomers,
  } = useGetCustomersQuery({ pageNumber })

  // Fetch expenses in the selected date range
  const {
    data: expensesData,
    isLoading: loadingExpenses,
    error: expensesError,
    refetch: refetchExpenses,
  } = useGetExpensesQuery({ startDate, endDate })

  const [deleteCustomer, { isLoading: loadingDelete }] = useDeleteCustomerMutation()
  const [createCustomer, { isLoading: loadingCreate }] = useCreateCustomerMutation()

  // --- CRUD Handlers ---
  const deleteHandler = async (id) => {
    if (!window.confirm('Delete this customer?')) return
    try {
      await deleteCustomer(id).unwrap()
      toast.success('Customer deleted')
      refetchCustomers()
    } catch (err) {
      toast.error(err?.data?.message || err.error)
    }
  }

  const createHandler = async () => {
    if (!window.confirm('Create default new customer?')) return
    try {
      await createCustomer({
        name:         'New Customer',
        telephone:    '0000000000',
        company:      'Default Company',
        serialNumber: '0000',
        address:      'Default Address',
      }).unwrap()
      toast.success('Customer created')
      refetchCustomers()
    } catch (err) {
      toast.error(err?.data?.message || err.error)
    }
  }

  // --- Sort & Filter Helpers ---
  const handleSort = (field) => {
    const nextOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc'
    setSortField(field)
    setSortOrder(nextOrder)
  }

  const filterAndSort = () => {
    let list = customerData?.customers || []
    if (filter) {
      const term = filter.toLowerCase()
      list = list.filter(
        c =>
          c.name?.toLowerCase().includes(term) ||
          c.company?.toLowerCase().includes(term) ||
          c.telephone?.includes(filter)
      )
    }
    if (sortField) {
      list.sort((a, b) =>
        sortOrder === 'asc'
          ? a[sortField].localeCompare(b[sortField])
          : b[sortField].localeCompare(a[sortField])
      )
    }
    return list
  }

  // --- Expense summing helpers ---
  // Per-customer
  const sumExpensesByAccountAndCategory = (accountName, category) =>
    expensesData?.expenses
      .filter(e => e.accountName === accountName && e.category === category)
      .reduce((sum, e) => sum + Number(e.amount || 0), 0) || 0

  // General totals
  const sumExpensesByCategory = (category) =>
    expensesData?.expenses
      .filter(e => e.category === category)
      .reduce((sum, e) => sum + Number(e.amount || 0), 0) || 0

  const totalSalesCredit  = sumExpensesByCategory('SalesCredit')
  const totalSalesPayment = sumExpensesByCategory('SalesPayment')
  const totalBalanceDue   = totalSalesCredit + totalSalesPayment
  const exportToExcel = () => {
  const data = filterAndSort().map(cust => {
    const credit  = sumExpensesByAccountAndCategory(cust.name, 'SalesCredit')
    const payment = sumExpensesByAccountAndCategory(cust.name, 'SalesPayment')
    const balance = credit + payment

    return {
      ID: cust._id,
      Name: cust.name,
      Company: cust.company,
      Telephone: cust.telephone,
      Serial: cust.serialNumber,
      Address: cust.address,
      SalesCredit: credit.toFixed(2),
      SalesPayment: payment.toFixed(2),
      BalanceDue: balance.toFixed(2),
    }
  })

  const worksheet = XLSX.utils.json_to_sheet(data)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Customers')
  XLSX.writeFile(workbook, 'CustomerList.xlsx')
}

  return (
    <>
      {/* Header + Create */}
      <Row className="align-items-center mb-3">
        <Col><h1>Customers</h1></Col>
        <Col className="text-end">
          <Button onClick={createHandler}>
            <FaPlus /> Create Customer
          </Button>
          <Button variant="success" onClick={exportToExcel}>
      📥 Export
          </Button>
          <Link to='/admin/customerimportscreen' className='btn btn-light my-3'>
          AddViaExcel
          </Link>
          <Link to='/admin/customereditimportscreen' className='btn btn-light my-3'>
          EditViaExcel
          </Link>
        </Col>
      </Row>

      {/* Filter, Date Pickers */}
      <Row className="mb-4">
        <Col md={3}>
          <Form.Control
            type="text"
            placeholder="Filter by name, company, or phone"
            value={filter}
            onChange={e => setFilter(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Label>Start Date</Form.Label>
          <Form.Control
            type="date"
            value={startDate}
            onChange={e => {
              setStartDate(e.target.value)
              refetchExpenses()
            }}
          />
        </Col>
        <Col md={3}>
          <Form.Label>End Date</Form.Label>
          <Form.Control
            type="date"
            value={endDate}
            onChange={e => {
              setEndDate(e.target.value)
              refetchExpenses()
            }}
          />
        </Col>
      </Row>

      {/* General Totals */}
      <Row className="mb-4">
        <Col md={4}>
          <h5>Total SalesCredit: {totalSalesCredit.toFixed(2)}</h5>
        </Col>
        <Col md={4}>
          <h5>Total SalesPayment: {totalSalesPayment.toFixed(2)}</h5>
        </Col>
        <Col md={4}>
          <h5>BalanceDue: {totalBalanceDue.toFixed(2)}</h5>
        </Col>
      </Row>

      {/* Loaders & Errors */}
      {(loadingCustomers || loadingCreate || loadingDelete || loadingExpenses) && <Loader />}

      {customersError ? (
        <Message variant="danger">{customersError.data?.message}</Message>
      ) : expensesError ? (
        <Message variant="danger">{expensesError.data?.message}</Message>
      ) : (
        <>
          {/* Customers Table */}
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th onClick={() => handleSort('name')}        style={{cursor:'pointer'}}>NAME</th>
                <th onClick={() => handleSort('company')}     style={{cursor:'pointer'}}>COMPANY</th>
                <th onClick={() => handleSort('telephone')}   style={{cursor:'pointer'}}>TELEPHONE</th>
                <th onClick={() => handleSort('serialNumber')}style={{cursor:'pointer'}}>SERIAL</th>
                <th>ADDRESS</th>
                <th>SalesCredit</th>
                <th>SalesPayment</th>
                <th>BalanceDue</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filterAndSort().map(cust => {
                const credit  = sumExpensesByAccountAndCategory(cust.name, 'SalesCredit')
                const payment = sumExpensesByAccountAndCategory(cust.name, 'SalesPayment')
                const balance = credit + payment

                return (
                  <tr key={cust._id}>
                    <td>{cust.name}</td>
                    <td>{cust.company}</td>
                    <td>{cust.telephone}</td>
                    <td>{cust.serialNumber}</td>
                    <td>{cust.address}</td>
                    <td>{credit.toFixed(2)}</td>
                    <td>{payment.toFixed(2)}</td>
                    <td style={{ color: balance > 0 ? 'red' : 'green' }}>
                      {balance.toFixed(2)}
                    </td>
                    <td>
                      <Button
                        as={Link}
                        to={`/admin/customer/${cust._id}/edit`}
                        variant="light"
                        className="btn-sm me-2"
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        variant="danger"
                        className="btn-sm"
                        onClick={() => deleteHandler(cust._id)}
                      >
                        <FaTrash style={{ color: 'white' }} />
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>

          {/* Pagination */}
          <Paginate
            pages={customerData?.pages || 1}
            page={ customerData?.page  || 1 }
            isAdmin
            type="customerlist"
          />
        </>
      )}
    </>
  )
}

export default CustomerListScreen